import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { debounce, isEqual } from 'lodash';
import { directionStyles } from '../models';
import { MapService } from '../state/services/map.service';
import {
    IDatiqMapGeoJsonFeature
} from '../state/services/models';

type LocationPayload = {
    id: string;
    loaded: boolean;
    featureCollection: IDatiqMapGeoJsonFeature;
    latLong: { lat: number; lng: number };
};

@Component({
    selector: 'iq-mc-map-lane-map',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-mc-map-lane-map.component.html',
    styleUrl: './iq-mc-map-lane-map.component.scss',
    providers: [
        MapService,
    ],
})
export class IqMcMapLaneMapComponent implements OnDestroy {
    id: InputSignal<string> = input('');

    private _map: MapService = inject(MapService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregister: Record<string, IqEventBusBrokerHandler> = {};

    originPayload: WritableSignal<LocationPayload> = signal<LocationPayload>({
        id: '',
        loaded: false,
        featureCollection: null,
        latLong: null,
    });
    originLatLong = computed(() => {
        return this.originPayload().latLong;
    });
    originGeo = computed(() => {
        return this.originPayload().featureCollection;
    });

    destinationPayload: WritableSignal<LocationPayload> = signal<LocationPayload>({
        id: '',
        loaded: false,
        featureCollection: null,
        latLong: null,
    });
    destinationLatLong = computed(() => {
        return this.destinationPayload().latLong;
    });
    destinationGeo = computed(() => {
        return this.destinationPayload().featureCollection;
    });

    mapEl: Signal<ElementRef> = viewChild('mapContainerEl');
    map: Signal<google.maps.Map> = computed(() => {
        const mapEl = this.mapEl();

        if (!mapEl) return null;

        return this._map.create(mapEl);
    });

    directions: WritableSignal<google.maps.DirectionsResult> = signal(null);

    directionsRenderer = computed(() => {
        const map = this.map();

        if (!map) return null;

        return new google.maps.DirectionsRenderer({
            map,
            polylineOptions: directionStyles,
            suppressMarkers: true,
        });
    });

    originData: WritableSignal<google.maps.Data> = signal(null);
    originMarker: WritableSignal<google.maps.Marker> = signal(null);
    destinationData: WritableSignal<google.maps.Data> = signal(null);
    destinationMarker: WritableSignal<google.maps.Marker> = signal(null);

    isLaneLoaded: Signal<boolean> = computed(() => {
        const originLoaded = this.originPayload().loaded;
        const destinationLoaded = this.destinationPayload().loaded;

        return originLoaded && destinationLoaded;
    }, { equal: isEqual });

    isMapLoaded: Signal<boolean> = computed(() => {
        const mapLoaded = !!this.map();

        return mapLoaded;
    }, { equal: isEqual });

    isLoaded: Signal<boolean> = computed(() => {
        return this.isLaneLoaded() && this.isMapLoaded();
    });

    constructor() {
        this._deregister = this._bus.registerMap({
            setMapLaneOriginLocation: {
                callback: (payload: LocationPayload) => {
                    if (this.id() !== payload.id) return;

                    this.originPayload.set(payload);
                },
            },
            setMapLaneDestinationLocation: {
                callback: (payload) => {
                    if (this.id() !== payload.id) return;

                    this.destinationPayload.set(payload);
                },
            },
            setDirections: {
                callback: (payload: any) => {
                    if (this.id() !== payload.id) return;

                    this.directions.set(payload.directions);
                }
            },
            updateMapLoadingStatus: {
                callback: (payload: any) => {
                    if (!payload.loading) return;

                    this.originPayload.set({
                        id: '',
                        loaded: false,
                        featureCollection: null,
                        latLong: null,
                    });
                    this.destinationPayload.set({
                        id: '',
                        loaded: false,
                        featureCollection: null,
                        latLong: null,
                    });
                }
            }
        });

        effect(() => {
            const map = this.map();
            const originLatLong = this.originLatLong();
            const destinationLatLong = this.destinationLatLong();

            if (
                !map ||
                !originLatLong ||
                !destinationLatLong
            )
                return;

            this._map.setCenter(map, originLatLong, destinationLatLong); 
        });

        effect(() => {
            const map = this.map();
            const renderer = this.directionsRenderer();
            const directions = this.directions();

            if (
                !map ||
                !renderer ||
                !directions
            )
                return;

            const oldOriginMarker = untracked(() => this.originMarker());
            const oldDestinationMarker = untracked(() => this.destinationMarker());

            oldOriginMarker?.setMap(null);
            oldDestinationMarker?.setMap(null);

            const { originMarker, destinationMarker } = this._map.renderDirections(map, directions, renderer);
            this.originMarker.set(originMarker);
            this.destinationMarker.set(destinationMarker);
        }, { allowSignalWrites: true });

        effect(() => {
            const map = this.map();
            const originGeo = this.originGeo();
            const destinationGeo = this.destinationGeo();

            if (
                !map ||
                !originGeo ||
                !destinationGeo
            )
                return;

            // untracked so we don't trigger infinite loops (orr.epstein)
            const originData = untracked(() => this.originData());
            const destinationData = untracked(() => this.destinationData());

            // clear previous data
            originData?.setMap(null);
            destinationData?.setMap(null);

            // set new data
            const { origin, destination } = this._map.setAreas(map, originGeo, destinationGeo);
            this.originData.set(origin);
            this.destinationData.set(destination);
        }, { allowSignalWrites: true });

        effect(() => {
            const loaded = this.isLoaded();

            if (!loaded) return;

            untracked(() => {        
                this._bus.dispatch({
                    name: 'rateviewLoadedMap',
                    payload: {
                        id: this.id(),
                    },
                });
            });
        });
    }

    ngOnDestroy() {
        this._bus.unregisterMap(this._deregister);
    }
}
